<template>
  <div class="operator-container" v-if="applicableOperators.length">
    <select
      class="operator-select"
      name="operator"
      :value="condition.operator || ''"
      @change="handleOperatorChange"
    >
      <option value="" disabled>{{ parseTranslation(OPERATOR) }}</option>

      <option
        v-for="operator in applicableOperators"
        :key="`${id}-${operator}`"
        :value="operator"
      >
        {{ getOptionLabel(operator) }}
      </option>
    </select>

    <div v-if="condition.operator" class="operator-input-container">
      <div
        class="single-input-container"
        v-for="singleConfig in operatorsConfig[condition.operator].inputConfig"
        :key="singleConfig.paramProperty"
      >
        <span class="input-joiner" v-if="singleConfig.joinerString">{{
          singleConfig.joinerString
        }}</span>

        <input
          :type="singleConfig.type || 'text'"
          :name="singleConfig.paramProperty"
          :class="{ reduced: singleConfig.reduced || false }"
          :value="
            (condition.params && condition.params[singleConfig.paramProperty]) || ''
          "
          @change="handleInputChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { questionsConfig, operatorsConfig } from '@/utils/questions'
import { removePropertiesFromObject } from '@/utils/object'

import * as translationIndex from './index.translations'
const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'OperatorRenderer',
  components: {},

  data() {
    return {
      translations,
      ...translationKeys,
      collapsePanel: false,
      operatorsConfig,
    }
  },

  props: {
    id: {
      required: true,
      type: String,
    },
    available_questions: {
      required: true,
      type: Array,
    },
    condition: {
      required: true,
      type: Object,
    },
    update_condition: {
      required: true,
      type: Function,
    },
    remove_self: {
      required: true,
      type: Function,
    },
  },

  computed: {
    questionRef() {
      return this.condition.questionRef
    },

    operator() {
      return this.condition.operator
    },

    formulaType() {
      return this.condition.type
    },

    params() {
      return this.condition.params
    },

    referencedQuestion() {
      const referenced = this.available_questions.find(
        ({ qid }) => qid === this.questionRef.id,
      )

      return referenced
    },

    questionCode() {
      return this.referencedQuestion?.code
    },

    questionType() {
      return this.referencedQuestion?.type
    },

    applicableOperators() {
      const allOperators = Object.keys(operatorsConfig)

      const validOperators =
        questionsConfig[this.questionType]?.formulaReferences?.[this.formulaType] || []

      const filteredByQuestionType = allOperators.filter(operator =>
        validOperators.includes(operator),
      )

      return filteredByQuestionType
    },
  },

  watch: {
    applicableOperators() {
      const isApplicable = this.applicableOperators.includes(this.operator)

      if (isApplicable || !this.operator) return

      this.resetConditionOperator()
    },
  },

  methods: {
    ...mapActions(['sendMessage']),

    getNewOperatorParams(operator) {
      const { inputConfig } = operatorsConfig[operator]

      if (!inputConfig) return null

      const paramsKept = Object.entries(this.condition.params || {}).reduce(
        (acc, [key, value]) => {
          const config = inputConfig.find(({ paramProperty }) => paramProperty === key)

          if (!config) return acc

          const { validator } = config

          if (!validator) return { ...acc, [key]: value }

          const { isValid, value: actualValue } = validator(value)

          if (!isValid) return acc

          return { ...acc, [key]: actualValue }
        },
        {},
      )

      return paramsKept
    },

    resetConditionOperator() {
      const newCondition = removePropertiesFromObject(this.condition, [
        'operator',
        'params',
      ])

      this.update_condition(newCondition)
    },

    handleOperatorChange({ target }) {
      const { value } = target

      const updatedParams = this.getNewOperatorParams(value)

      const updatedCondition = {
        ...this.condition,
        operator: value,
        params: updatedParams,
      }

      this.update_condition(updatedCondition)
    },

    handleInputChange({ target }) {
      const { name, value } = target

      const updatedParams = {
        ...(this.params || {}),
        [name]: value,
      }

      const updatedCondition = {
        ...this.condition,
        params: updatedParams,
      }

      this.update_condition(updatedCondition)
    },

    getOptionLabel(operator) {
      return (
        operatorsConfig[operator].displayText?.[this.language] ||
        operatorsConfig[operator].displayText
      )
    },
  },
}
</script>

<style scoped lang="scss">
.operator-container,
.operator-input-container,
.single-input-container {
  @include flexAlignCenter;
}

select {
  @include baseSelect;

  option {
    color: $gray-2;
  }
}

select,
input {
  margin-left: 6px;
}

input {
  padding: 5px 8px;
  border: 1px solid $gray-9;
  border-radius: 3px;
  background: $transparent;
  border-color: $white;
  color: $white;
  outline: none;

  &.reduced {
    width: 100px;
  }

  .light & {
    background: $white;
    border-color: $gray-9;
    color: $gray-2;
  }
}

.input-joiner {
  margin-left: 6px;
  color: $gray-9;
  font-family: 'PT Sans', 'sans-serif', monospace;
}
</style>
